import type { JSX } from 'react'

import {
  FIELD_NAMES,
  FIELD_REGISTRY,
  operatorsFor,
  validateCondition,
  type Condition,
} from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { MoneyInputError, formatMinorForInput, parseMajorInput } from '../money.js'

/**
 * Appendix X.1 conditions, built from a list of comparisons rather than typed as JSON.
 *
 * **Why a builder and not a text box.** X.1 calls the language "not a scripting language, a data
 * structure", and the field registry is a closed set of 32 names with a type each. So the only
 * things a rule author can say are which field, which of the operators that field's type allows,
 * and a value of that type. A text box would let a shopkeeper write a tree that
 * `validateCondition` refuses, and X.4 puts that refusal at save time for a reason: a rule naming a
 * field that does not exist "would silently never match, which is indistinguishable from a
 * promotion nobody redeemed".
 *
 * **Flat and ANDed.** Every row must match, which is `{ all: [...] }`, or a single row on its own.
 * `any` and `none` are in the language and are not offered here: a shopkeeper writing "frames or
 * sunglasses" wants `line.category in [frames, sunglasses]`, which is one row. A condition this
 * cannot express is carried through a revision untouched rather than silently flattened, and the
 * screen says so.
 *
 * **A money value is minor units as a JSON number.** That is Appendix X's own shape: the registry
 * notes "minor units" and `validateValue` requires a number. The amount is typed in major units
 * like every other amount on the till and converted here, and the engine compares a `bigint` fact
 * against a `number` operand deliberately, which `sameValue` exists to do.
 *
 * **`between` is offered on money and number only.** `operatorsFor` allows it on a date too, and
 * `validateValue` then requires both bounds to be numbers, so a date range is a shape the language
 * accepts in one half and refuses in the other. Not offered rather than offered and refused.
 */

export interface ConditionRow {
  readonly field: string
  readonly op: string
  readonly value: string
  /** The upper bound, for `between`. */
  readonly upper: string
}

export const EMPTY_CONDITION_ROW: ConditionRow = { field: '', op: '=', value: '', upper: '' }

/** Thrown with a sentence a shopkeeper can act on. The server refuses the same shapes. */
export class ConditionInputError extends Error {}

const typeOf = (field: string): string =>
  (FIELD_REGISTRY[field] as { type: string } | undefined)?.type ?? 'string'

export const operatorsForField = (field: string): readonly string[] =>
  field === '' ? [] : operatorsFor(typeOf(field) as never)

/**
 * The operators a field is offered, which is not everything its type allows.
 *
 * `between` on a date is dropped because `operatorsFor` allows it and `validateValue` then requires
 * two numbers, so a date range is accepted by one half of the language and refused by the other.
 *
 * `in` and `not_in` are dropped on a **money** field, and that one is a defect this screen had. The
 * value box for a list is split on commas, and the money hint tells a shopkeeper to write the
 * amount "as you would write it on a receipt", which in this app is grouped: `1,500` split into
 * `1` and `500` is a condition on PKR 1.00 and PKR 500.00 that validates, stores, and then never
 * fires, which X.4 calls indistinguishable from a promotion nobody redeemed. A set of exact money
 * amounts is a strange rule anyway; a range is what people mean, and `between`, `>=` and `<=` are
 * all still offered.
 */
export const operatorsOffered = (field: string): readonly string[] =>
  operatorsForField(field).filter(
    (op) =>
      (op !== 'between' || isNumeric(field)) &&
      !(typeOf(field) === 'money' && (op === 'in' || op === 'not_in')),
  )

const isNumeric = (field: string): boolean =>
  typeOf(field) === 'money' || typeOf(field) === 'number'

export const takesNoValue = (op: string): boolean => op === 'exists'
export const takesList = (op: string): boolean => op === 'in' || op === 'not_in'
export const takesTwo = (op: string): boolean => op === 'between'

function scalar(field: string, raw: string, currency: string): unknown {
  const text = raw.trim()
  if (text === '') {
    throw new ConditionInputError(`${field} needs a value.`)
  }
  switch (typeOf(field)) {
    case 'money':
      try {
        // Minor units, as a number, which is what Appendix X's registry and validator ask for.
        return Number(parseMajorInput(text, currency))
      } catch (error) {
        throw new ConditionInputError(
          error instanceof MoneyInputError ? error.message : `${text} is not an amount.`,
        )
      }
    case 'number': {
      const value = Number(text)
      if (!Number.isFinite(value)) throw new ConditionInputError(`${text} is not a number.`)
      return value
    }
    case 'boolean':
      if (text !== 'true' && text !== 'false') {
        throw new ConditionInputError(`${field} is yes or no.`)
      }
      return text === 'true'
    default:
      return text
  }
}

/**
 * The rows as Appendix X JSON, or `undefined` when there are none.
 *
 * An absent condition is a promotion that always matches, which is what a shop's "10% off
 * everything this week" is, so no rows is a real answer rather than an error.
 */
export function toCondition(rows: readonly ConditionRow[], currency: string): unknown {
  const used = rows.filter((row) => row.field !== '')
  if (used.length === 0) return undefined

  const comparisons = used.map((row) => {
    if (!FIELD_NAMES.includes(row.field)) {
      throw new ConditionInputError(`${row.field} is not a field.`)
    }
    if (takesNoValue(row.op)) return { field: row.field, op: row.op }
    if (takesList(row.op)) {
      const parts = row.value
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== '')
      if (parts.length === 0) {
        throw new ConditionInputError(
          `${row.field} needs at least one value. An empty list matches nothing, silently.`,
        )
      }
      return {
        field: row.field,
        op: row.op,
        value: parts.map((part) => scalar(row.field, part, currency)),
      }
    }
    if (takesTwo(row.op)) {
      return {
        field: row.field,
        op: row.op,
        value: [scalar(row.field, row.value, currency), scalar(row.field, row.upper, currency)],
      }
    }
    return { field: row.field, op: row.op, value: scalar(row.field, row.value, currency) }
  })

  const condition = comparisons.length === 1 ? comparisons[0] : { all: comparisons }
  // The same validator the server runs at save, so a refusal is a sentence here rather than a
  // queued write that comes back 422.
  validateCondition(condition)
  return condition
}

/**
 * An existing condition as rows, or `null` when it is a shape this builder cannot express.
 *
 * Null is not an error. It means the condition was written as something richer than a flat AND and
 * a revision carries it through unchanged, which is better than flattening somebody's `any` into an
 * `all` and charging different prices as a result.
 */
export function rowsFromCondition(condition: unknown, currency: string): ConditionRow[] | null {
  if (condition === undefined || condition === null) return []
  const nodes = Array.isArray((condition as { all?: unknown[] }).all)
    ? ((condition as { all: unknown[] }).all as unknown[])
    : [condition]

  const rows: ConditionRow[] = []
  for (const node of nodes) {
    const record = node as Record<string, unknown>
    if (typeof record['field'] !== 'string' || typeof record['op'] !== 'string') return null
    const field = record['field']
    const op = record['op']
    const show = (value: unknown): string =>
      typeOf(field) === 'money' && typeof value === 'number'
        ? // Major units, bare: `formatMoney` is the ungrouped digit form, which is what
          // `parseMajorInput` takes back. Grouped, a revision of a promotion with an amount in its
          // condition was refused with "Not a valid amount: 15,000.00", which the round-trip case
          // in `condition-builder.test.ts` found.
          formatMinorForInput(BigInt(Math.trunc(value)), currency)
        : String(value)

    if (takesNoValue(op)) {
      rows.push({ field, op, value: '', upper: '' })
      continue
    }
    const value = record['value']
    if (takesTwo(op) && Array.isArray(value) && value.length === 2) {
      rows.push({ field, op, value: show(value[0]), upper: show(value[1]) })
      continue
    }
    if (takesList(op) && Array.isArray(value)) {
      rows.push({ field, op, value: value.map(show).join(', '), upper: '' })
      continue
    }
    if (Array.isArray(value)) return null
    rows.push({ field, op, value: show(value), upper: '' })
  }
  return rows
}

export interface ConditionBuilderProps {
  readonly rows: readonly ConditionRow[]
  readonly onChange: (rows: readonly ConditionRow[]) => void
  readonly currency: string
}

export function ConditionBuilder({ rows, onChange, currency }: ConditionBuilderProps): JSX.Element {
  const { t } = useTranslation()

  const update = (index: number, next: Partial<ConditionRow>): void => {
    onChange(
      rows.map((row, i) => {
        if (i !== index) return row
        const merged = { ...row, ...next }
        // A field change can leave an operator its new type does not allow, which the server would
        // refuse. Reset to the first operator the type does allow.
        if (next.field !== undefined && !operatorsOffered(merged.field).includes(merged.op)) {
          return { ...merged, op: operatorsOffered(merged.field)[0] ?? '=' }
        }
        return merged
      }),
    )
  }

  return (
    <fieldset className="lm-condition">
      <legend className="lm-condition__legend">{t('condition.legend')}</legend>
      <p className="lm-hint">{t('condition.allMustMatch')}</p>

      {rows.map((row, index) => (
        <div className="lm-condition__row" key={`${String(index)}-${row.field}`}>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('condition.field', { number: index + 1 })}</span>
            <select
              className="lm-input"
              value={row.field}
              onChange={(e) => {
                const value = e.currentTarget.value
                update(index, { field: value })
              }}
            >
              <option value="">{t('condition.anySale')}</option>
              {FIELD_NAMES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          {row.field === '' ? null : (
            <>
              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">
                  {t('condition.operator', { number: index + 1 })}
                </span>
                <select
                  className="lm-input"
                  value={row.op}
                  onChange={(e) => {
                    const value = e.currentTarget.value
                    update(index, { op: value })
                  }}
                >
                  {operatorsOffered(row.field).map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </label>

              {takesNoValue(row.op) ? null : (
                <label className="lm-field lm-field--inline">
                  <span className="lm-field__label">
                    {takesList(row.op)
                      ? t('condition.valueList', { number: index + 1 })
                      : t('condition.value', { number: index + 1 })}
                  </span>
                  {typeOf(row.field) === 'boolean' && !takesList(row.op) ? (
                    <select
                      className="lm-input"
                      value={row.value}
                      onChange={(e) => {
                        const value = e.currentTarget.value
                        update(index, { value })
                      }}
                    >
                      <option value="">{t('condition.chooseYesNo')}</option>
                      <option value="true">{t('condition.yes')}</option>
                      <option value="false">{t('condition.no')}</option>
                    </select>
                  ) : (
                    <input
                      className="lm-input"
                      autoComplete="off"
                      value={row.value}
                      onChange={(e) => {
                        const value = e.currentTarget.value
                        update(index, { value })
                      }}
                    />
                  )}
                </label>
              )}

              {takesTwo(row.op) ? (
                <label className="lm-field lm-field--inline">
                  <span className="lm-field__label">
                    {t('condition.upper', { number: index + 1 })}
                  </span>
                  <input
                    className="lm-input"
                    autoComplete="off"
                    value={row.upper}
                    onChange={(e) => {
                      const value = e.currentTarget.value
                      update(index, { upper: value })
                    }}
                  />
                </label>
              ) : null}

              {typeOf(row.field) === 'money' ? (
                // The registry says minor units and the till types major ones, so the label says
                // which: 500 means PKR 500.00 and not five rupees.
                <span className="lm-hint">{t('condition.moneyIn', { currency })}</span>
              ) : null}
            </>
          )}

          <button
            type="button"
            className="lm-button lm-button--secondary"
            onClick={() => {
              onChange(rows.filter((_, i) => i !== index))
            }}
          >
            {t('condition.remove')}
          </button>
        </div>
      ))}

      <button
        type="button"
        className="lm-button lm-button--secondary"
        onClick={() => {
          onChange([...rows, EMPTY_CONDITION_ROW])
        }}
      >
        {t('condition.add')}
      </button>
    </fieldset>
  )
}

/** Re-exported so a caller can narrow what it stores without importing the domain itself. */
export type { Condition }
